// AIActionsController
// Handles AI actions: summarize, extract tags, classify
// Supports both toolbar buttons and info panel buttons

import { ChromeAIDetector } from '../services/chrome-ai-detector';
import { HybridSummarizer } from '../services/hybrid-summarizer';
import { HybridTagger } from '../services/hybrid-tagger';
import { HybridClassifier } from '../services/hybrid-classifier';
import type { Document } from '../services/api';

export interface AIActionCallbacks {
  saveFieldValue: (field: string, value: any, skipRender?: boolean) => Promise<void>;
  renderDocInfoFields: () => void;
  getCurrentDoc: () => Document | null;
  getKnowledgePreviewDoc: () => any | null;
}

export class AIActionsController {
  private callbacks: AIActionCallbacks;

  constructor(callbacks: AIActionCallbacks) {
    this.callbacks = callbacks;
  }

  // Get document from Notes or Knowledge preview
  private getActiveDoc(): { doc: Document | any; content: string; title: string; tags: string[] } | null {
    const doc = this.callbacks.getCurrentDoc() || this.callbacks.getKnowledgePreviewDoc();
    if (!doc) return null;

    const content = 'content' in doc ? doc.content : (doc as any).content;
    const title = 'meta' in doc ? doc.meta.title : doc.title;
    const meta = 'meta' in doc ? doc.meta : doc;
    const tags = meta.tags || [];

    if (!content) return null;

    return { doc, content, title, tags };
  }

  // Update AI status badge (Chrome AI availability)
  async updateAIStatusBadge(): Promise<void> {
    const badge = document.getElementById('ai-status-badge');
    if (!badge) return;

    try {
      const availability = await ChromeAIDetector.getCached();
      const hasAI = availability.summarizer?.canUse || availability.writer?.canUse;

      if (hasAI) {
        badge.textContent = '✓ Available';
        badge.className = 'ai-status available';
      } else if (availability.summarizer?.downloading || availability.writer?.downloading) {
        badge.textContent = 'Downloading...';
        badge.className = 'ai-status downloading';
      } else {
        badge.textContent = 'Cloud Only';
        badge.className = 'ai-status cloud';
      }
    } catch (error) {
      badge.textContent = 'Cloud Only';
      badge.className = 'ai-status cloud';
    }
  }

  // Update AI actions visibility based on current view
  updateVisibility(currentView: string, hasDoc: boolean, hasKnowledgePreview: boolean): void {
    const aiActionsGroup = document.getElementById('ai-actions-group');
    if (!aiActionsGroup) return;

    const hasNotesDoc = currentView === 'notes' && hasDoc;
    const hasPreview = currentView === 'knowledge' && hasKnowledgePreview;
    const shouldShow = hasNotesDoc || hasPreview;

    if (shouldShow) {
      aiActionsGroup.classList.remove('hidden');
    } else {
      aiActionsGroup.classList.add('hidden');
    }
  }

  // Show method badge (on-device vs cloud)
  private showMethodBadge(btn: HTMLButtonElement, method: 'on-device' | 'cloud', cost: number): void {
    const badge = btn.querySelector('.ai-method-badge') as HTMLElement;
    if (!badge) return;

    if (method === 'on-device') {
      badge.textContent = '💚 FREE';
      badge.className = 'ai-method-badge on-device';
    } else {
      badge.textContent = `☁️ $${cost.toFixed(4)}`;
      badge.className = 'ai-method-badge cloud';
    }

    badge.style.display = 'inline-block';

    setTimeout(() => {
      badge.style.display = 'none';
    }, 3000);
  }

  // ========== TOOLBAR BUTTON HANDLERS ==========

  async handleSummarizeButton(): Promise<void> {
    const active = this.getActiveDoc();
    if (!active) {
      alert('Please open a document first');
      return;
    }

    const btn = document.getElementById('btn-ai-summarize') as HTMLButtonElement;
    if (!btn) return;

    btn.classList.add('processing');
    btn.disabled = true;

    try {
      const result = await HybridSummarizer.summarize(active.content, {
        type: 'key-points',
        length: 'short',
        context: active.title
      });

      await this.callbacks.saveFieldValue('summary', result.summary);

      btn.classList.remove('processing');
      btn.classList.add('success');

      console.log(`✅ Summary generated (${result.method}, ${result.duration}ms, $${result.cost.toFixed(4)})`);

      setTimeout(() => {
        btn.classList.remove('success');
        btn.disabled = false;
      }, 2000);

    } catch (error: any) {
      console.error('Failed to generate summary:', error);
      alert('Failed to generate summary: ' + error.message);
      btn.classList.remove('processing');
      btn.disabled = false;
    }
  }

  async handleExtractTagsButton(): Promise<void> {
    const active = this.getActiveDoc();
    if (!active) {
      alert('Please open a document first');
      return;
    }

    const btn = document.getElementById('btn-ai-tags') as HTMLButtonElement;
    if (!btn) return;

    btn.classList.add('processing');
    btn.disabled = true;

    try {
      const result = await HybridTagger.extractTags(active.content, active.title);

      const newTags = [...new Set([...active.tags, ...result.tags])];
      await this.callbacks.saveFieldValue('tags', newTags);

      btn.classList.remove('processing');
      btn.classList.add('success');

      console.log(`✅ Tags extracted (${result.method}, ${result.tags.length} tags, ${result.duration}ms, $${result.cost.toFixed(4)})`);

      setTimeout(() => {
        btn.classList.remove('success');
        btn.disabled = false;
      }, 2000);

    } catch (error: any) {
      console.error('Failed to extract tags:', error);
      alert('Failed to extract tags: ' + error.message);
      btn.classList.remove('processing');
      btn.disabled = false;
    }
  }

  async handleClassifyButton(): Promise<void> {
    const active = this.getActiveDoc();
    if (!active) {
      alert('Please open a document first');
      return;
    }

    const btn = document.getElementById('btn-ai-classify') as HTMLButtonElement;
    if (!btn) return;

    btn.classList.add('processing');
    btn.disabled = true;

    try {
      const result = await HybridClassifier.classify(active.content, active.title);

      if (result.type) await this.callbacks.saveFieldValue('type', result.type, true);
      if (result.tags && result.tags.length > 0) {
        const newTags = [...new Set([...active.tags, ...result.tags])];
        await this.callbacks.saveFieldValue('tags', newTags, true);
      }
      if (result.resourceCategory) await this.callbacks.saveFieldValue('resourceCategory', result.resourceCategory, true);
      if (result.project) await this.callbacks.saveFieldValue('project', result.project, true);
      if (result.area) await this.callbacks.saveFieldValue('area', result.area, true);
      if (result.summary) await this.callbacks.saveFieldValue('summary', result.summary, true);
      if (result.contentFormat) await this.callbacks.saveFieldValue('contentFormat', result.contentFormat, true);
      if (result.platform) await this.callbacks.saveFieldValue('platform', result.platform, true);

      this.callbacks.renderDocInfoFields();

      btn.classList.remove('processing');
      btn.classList.add('success');

      console.log(`✅ Content classified (${result.method}, ${result.duration}ms, $${result.cost.toFixed(4)})`);

      setTimeout(() => {
        btn.classList.remove('success');
        btn.disabled = false;
      }, 2000);

    } catch (error: any) {
      console.error('Failed to classify:', error);
      alert('Failed to classify: ' + error.message);
      btn.classList.remove('processing');
      btn.disabled = false;
    }
  }

  // ========== INFO PANEL BUTTON HANDLERS ==========

  async handleSummarizePanel(): Promise<void> {
    const doc = this.callbacks.getCurrentDoc();
    if (!doc) return;

    const btn = document.getElementById('ai-btn-summarize') as HTMLButtonElement;
    if (!btn) return;

    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="material-symbols-outlined spinning">progress_activity</span><span>Generating...</span>';

    try {
      const result = await HybridSummarizer.summarize(doc.content, {
        type: 'key-points',
        length: 'short',
        context: doc.meta.title
      });

      await this.callbacks.saveFieldValue('summary', result.summary);
      this.showMethodBadge(btn, result.method, result.cost);

      console.log(`✅ Summary generated (${result.method}, ${result.duration}ms, $${result.cost.toFixed(4)})`);

    } catch (error: any) {
      console.error('Failed to generate summary:', error);
      alert('Failed to generate summary: ' + error.message);
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  }

  async handleExtractTagsPanel(): Promise<void> {
    const doc = this.callbacks.getCurrentDoc();
    if (!doc) return;

    const btn = document.getElementById('ai-btn-tags') as HTMLButtonElement;
    if (!btn) return;

    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="material-symbols-outlined spinning">progress_activity</span><span>Extracting...</span>';

    try {
      const result = await HybridTagger.extractTags(doc.content, doc.meta.title);

      const currentTags = doc.meta.tags || [];
      const newTags = [...new Set([...currentTags, ...result.tags])];
      await this.callbacks.saveFieldValue('tags', newTags);

      this.showMethodBadge(btn, result.method, result.cost);

      console.log(`✅ Tags extracted (${result.method}, ${result.tags.length} tags, ${result.duration}ms, $${result.cost.toFixed(4)})`);

    } catch (error: any) {
      console.error('Failed to extract tags:', error);
      alert('Failed to extract tags: ' + error.message);
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  }

  async handleClassifyPanel(): Promise<void> {
    const doc = this.callbacks.getCurrentDoc();
    if (!doc) return;

    const btn = document.getElementById('ai-btn-classify') as HTMLButtonElement;
    if (!btn) return;

    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="material-symbols-outlined spinning">progress_activity</span><span>Classifying...</span>';

    try {
      const result = await HybridClassifier.classify(doc.content, doc.meta.title);

      if (result.type) await this.callbacks.saveFieldValue('type', result.type, true);
      if (result.tags && result.tags.length > 0) {
        const currentTags = doc.meta.tags || [];
        const newTags = [...new Set([...currentTags, ...result.tags])];
        await this.callbacks.saveFieldValue('tags', newTags, true);
      }
      if (result.resourceCategory) await this.callbacks.saveFieldValue('resourceCategory', result.resourceCategory, true);
      if (result.project) await this.callbacks.saveFieldValue('project', result.project, true);
      if (result.area) await this.callbacks.saveFieldValue('area', result.area, true);
      if (result.summary) await this.callbacks.saveFieldValue('summary', result.summary, true);
      if (result.contentFormat) await this.callbacks.saveFieldValue('contentFormat', result.contentFormat, true);
      if (result.platform) await this.callbacks.saveFieldValue('platform', result.platform, true);

      this.callbacks.renderDocInfoFields();
      this.showMethodBadge(btn, result.method, result.cost);

      console.log(`✅ Content classified (${result.method}, ${result.duration}ms, $${result.cost.toFixed(4)})`);

    } catch (error: any) {
      console.error('Failed to classify:', error);
      alert('Failed to classify: ' + error.message);
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  }

  // Setup event listeners for info panel AI buttons
  setupInfoPanelListeners(): void {
    const summarizeBtn = document.getElementById('ai-btn-summarize');
    const tagsBtn = document.getElementById('ai-btn-tags');
    const classifyBtn = document.getElementById('ai-btn-classify');

    summarizeBtn?.addEventListener('click', () => this.handleSummarizePanel());
    tagsBtn?.addEventListener('click', () => this.handleExtractTagsPanel());
    classifyBtn?.addEventListener('click', () => this.handleClassifyPanel());
  }
}
